import { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Brain, Upload, RefreshCw, LayoutDashboard, Landmark } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import StatsCards from '../components/dashboard/StatsCards';
import SpendingByCategoryChart from '../components/dashboard/SpendingByCategoryChart';
import IncomeByCategoryChart from '../components/dashboard/IncomeByCategoryChart';
import MonthlyTrendChart from '../components/dashboard/MonthlyTrendChart';
import MonthlyIncomeTrendChart from '../components/dashboard/MonthlyIncomeTrendChart';
import FilterBar from '../components/dashboard/FilterBar';
import AIInsightsPanel from '../components/insights/AIInsightsPanel';
import TransactionDrillModal from '../components/dashboard/TransactionDrillModal';
import { apiClient } from '@/api/apiClient';
import Wealth from './Wealth';

const now = new Date();
const defaultFY = (now.getMonth() + 1) >= 7 ? now.getFullYear() + 1 : now.getFullYear();

const MORTGAGE_CATEGORIES = new Set([
  'Transfer - ANZ Offset',
  'Transfer - CBA',
  'Transfer - ME Transaction',
]);

const SHOPPING_CATEGORIES = new Set([
  'Dan Shopping',
  'Sarah Shopping',
  'Online Shopping',
  'Homewares',
]);
const SHOPPING_LABEL = 'Shopping';

const EXCLUDED_CATEGORIES = new Set([
  'Credit Card Repayments',
  'Transfer - Investments',
  'Transfer - School Fees',
  'Transfer - Sue Martin',
  'Transfer - Robinsons',
  'Transfer - Holidays',
  'Transfer - Cameron Savings',
  'Transfer - Utilities',
  'Transfer - Deferred Wages',
  'Transfer - Rates',
  'Transfer - Rego',
  'Transfer - No Touchy',
  'Transfer - Alice Savings',
]);

const EXCLUDED_INCOME_CATEGORIES = new Set([
  'Transfer - ANZ Offset',
]);

function formatSyncTime(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  const aest = new Date(d.getTime() + 10 * 60 * 60 * 1000);
  return aest.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }) + ' AEST';
}

// ---------------------------------------------------------------------------
// Tab switcher
// ---------------------------------------------------------------------------
function DashboardTabs({ active, onChange }) {
  const tabs = [
    { key: 'spending', label: 'Spending', icon: LayoutDashboard },
    { key: 'wealth', label: 'Wealth', icon: Landmark },
  ];
  return (
    <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
      {tabs.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            active === key
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spending dashboard
// ---------------------------------------------------------------------------
function SpendingDashboard() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    periodType: 'financial_year',
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    financialYear: defaultFY,
    categories: [],
  });
  const [showInsights, setShowInsights] = useState(false);
  const [showDrill, setShowDrill] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const { data: budgets = [] } = useQuery({
    queryKey: ['budgets'],
    queryFn: () => base44.entities.Budget.list('category'),
  });

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ['transactions'],
    queryFn: async () => {
      const PAGE = 5000;
      let all = [];
      let skip = 0;
      for (;;) {
        const page = await base44.entities.Transaction.list('-date', PAGE, skip);
        all = all.concat(page);
        if (page.length < PAGE) break;
        skip += PAGE;
      }
      return all;
    },
  });

  const { data: lastUpload } = useQuery({
    queryKey: ['lastUpload'],
    queryFn: async () => {
      const latest = await base44.entities.Transaction.list('-created_date', 1);
      return latest[0]?.created_date || null;
    },
  });

  const { data: syncStatus, refetch: refetchSyncStatus } = useQuery({
    queryKey: ['syncStatus'],
    queryFn: () => apiClient.get('/sync/status'),
    refetchInterval: syncMessage === 'running' ? 5000 : false,
  });

  const triggerSync = useMutation({
    mutationFn: () => apiClient.post('/sync/run', {}),
    onMutate: () => setSyncMessage('running'),
    onSuccess: () => {
      setSyncMessage('running');
      const poll = setInterval(async () => {
        const status = await refetchSyncStatus();
        const latest = status?.data;
        if (latest?.status === 'success' || latest?.status === 'error') {
          clearInterval(poll);
          setSyncMessage(latest.status === 'success' ? 'done' : 'error');
          if (latest.status === 'success') {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['lastUpload'] });
          }
          setTimeout(() => setSyncMessage(null), 8000);
        }
      }, 4000);
      setTimeout(() => clearInterval(poll), 90_000);
    },
    onError: () => {
      setSyncMessage('error');
      setTimeout(() => setSyncMessage(null), 6000);
    },
  });

  const filteredTransactions = useMemo(() => {
    let data = [...transactions];
    if (filters.periodType === 'month') {
      data = data.filter(t => t.month === filters.month && t.year === filters.year);
    } else if (filters.periodType === 'year') {
      data = data.filter(t => t.year === filters.year);
    } else if (filters.periodType === 'financial_year') {
      const fy = filters.financialYear;
      data = data.filter(t =>
        (t.month >= 7 && t.year === fy - 1) || (t.month <= 6 && t.year === fy)
      );
    } else if (filters.periodType === 'last_12_months') {
      const curYear = new Date().getFullYear();
      const curMonth = new Date().getMonth() + 1;
      data = data.filter(t => {
        const tVal = t.year * 12 + t.month;
        const startVal = (curYear - 1) * 12 + curMonth + 1;
        const endVal = curYear * 12 + curMonth;
        return tVal >= startVal && tVal <= endVal;
      });
    }
    data = data.filter(t => !EXCLUDED_CATEGORIES.has(t.category));
    data = data.filter(t => !(t.type === 'credit' && EXCLUDED_INCOME_CATEGORIES.has(t.category)));
    data = data.map(t => MORTGAGE_CATEGORIES.has(t.category) ? { ...t, category: 'Mortgage Repayments' } : t);
    data = data.map(t => SHOPPING_CATEGORIES.has(t.category) ? { ...t, category: SHOPPING_LABEL } : t);
    if (filters.categories.length > 0) {
      data = data.filter(t => filters.categories.includes(t.category));
    }
    return data;
  }, [transactions, filters]);

  const allCategories = useMemo(() => {
    return [...new Set(transactions.map(t => t.category).filter(Boolean))].sort();
  }, [transactions]);

  // Ids of the transactions currently visible on the dashboard. The drill
  // modal needs IDS rather than `filteredTransactions` themselves, because
  // those objects have had their categories rewritten for display (mortgage
  // and shopping aggregation) and must never be saved back in that form.
  const visibleIds = useMemo(
    () => new Set(filteredTransactions.map(t => t.id)),
    [filteredTransactions]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center px-6">
        <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
          <Upload className="w-7 h-7 text-emerald-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">No transactions yet</h2>
        <p className="text-slate-500 text-sm mb-6 max-w-sm">Upload your bank CSV or sync from your bank to start tracking.</p>
        <div className="flex gap-3">
          <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
            <Link to={createPageUrl('Upload')}>Upload CSV</Link>
          </Button>
          <Button variant="outline" onClick={() => triggerSync.mutate()} disabled={triggerSync.isPending}>
            <RefreshCw className="w-4 h-4 mr-2" />Sync Bank
          </Button>
        </div>
      </div>
    );
  }

  const isSyncing = triggerSync.isPending || syncMessage === 'running';
  const lastSyncTime = syncStatus?.created_date ? formatSyncTime(syncStatus.created_date) : null;
  const lastSyncOk = syncStatus?.status === 'success';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">{transactions.length.toLocaleString()} transactions total</p>
          {lastUpload && (
            <p className="text-xs text-slate-400 mt-0.5">
              Last upload: {new Date(new Date(lastUpload).getTime() + 10 * 60 * 60 * 1000).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })} AEST
            </p>
          )}
          {lastSyncTime && (
            <p className={`text-xs mt-0.5 ${lastSyncOk ? 'text-emerald-500' : 'text-rose-400'}`}>
              Last sync: {lastSyncTime}
              {syncStatus?.imported > 0 && ` · ${syncStatus.imported} imported`}
              {!lastSyncOk && syncStatus?.message && ` · ${syncStatus.message}`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {syncMessage && syncMessage !== 'running' && (
            <span className={`text-xs px-2.5 py-1.5 rounded-lg font-medium ${
              syncMessage === 'done' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
            }`}>
              {syncMessage === 'done' ? `✓ Synced — ${syncStatus?.imported ?? 0} new` : '✗ Sync failed'}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => triggerSync.mutate()} disabled={isSyncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing…' : 'Sync Bank'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowDrill(true)}>Drill Into</Button>
          <Button
            onClick={() => setShowInsights(v => !v)}
            variant={showInsights ? 'outline' : 'default'}
            className={showInsights ? '' : 'bg-violet-600 hover:bg-violet-700'}
            size="sm"
          >
            <Brain className="w-4 h-4 mr-2" />
            {showInsights ? 'Hide Insights' : 'AI Insights'}
          </Button>
        </div>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} allCategories={allCategories} />
      <StatsCards transactions={filteredTransactions} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SpendingByCategoryChart transactions={filteredTransactions} budgets={budgets} filters={filters} />
        <IncomeByCategoryChart transactions={filteredTransactions} />
        <MonthlyTrendChart transactions={filteredTransactions} budgets={budgets} filters={filters} allTransactions={transactions} />
        <MonthlyIncomeTrendChart transactions={filteredTransactions} allTransactions={transactions} filters={filters} />
      </div>

      {showInsights && (
        <AIInsightsPanel
          transactions={filteredTransactions}
          budgets={budgets}
          onClose={() => setShowInsights(false)}
        />
      )}

      {showDrill && (
        <TransactionDrillModal
          transactions={transactions}
          visibleIds={visibleIds}
          categories={allCategories}
          onClose={() => setShowDrill(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('spending');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-slate-800">Dashboard</h1>
        <DashboardTabs active={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === 'spending' ? <SpendingDashboard /> : <Wealth />}
    </div>
  );
}
